import { A11y, Keyboard, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { defineMessages, useIntl } from 'react-intl';
import config from '@plone/volto/registry';

import styles from '../styles.module.css';
import CTA from '../../commons/CTA';
import type { TestimonialsData } from '../schema';

interface TestimonialSwiperProps {
  data: TestimonialsData['testimonials'];
  editMode?: boolean;
}

const TestimonialSwiper = ({
  data,
  editMode = false,
}: TestimonialSwiperProps) => {
  const intl = useIntl();

  const swiperConfig = {
    modules: [Navigation, A11y, Keyboard],
    navigation: {
      enabled: true,
    },
    loop: true,
    centeredSlides: true,
    simulateTouch: true,
    lazy: true,
    keyboard: {
      enabled: true,
      onlyInViewport: true,
    },
    className: 'testimonial-carousel',
    a11y: {
      enabled: true,
    },
    slidesPerView: 1,
    spaceBetween: '30',
    autoHeight: true,
  };

  const Image = config.getComponent('Image').component;

  return (
    <div style={{ position: 'relative' }}>
      <Swiper {...swiperConfig}>
        {data.map((item, i) => {
          const { testimonial } = item;

          const testimonial_owner = [
            testimonial?.person_name,
            testimonial?.person_position,
            testimonial?.vending_place,
          ]
            .filter(Boolean)
            .join(', ');

          return (
            <SwiperSlide key={item['@id']}>
              <div className={styles.slideWrapper}>
                {testimonial && (
                  <div className={styles.slideCard}>
                    <div className={styles.cardHeader}>
                      <div className={styles.cardImageContainer}>
                        <Image
                          item={testimonial}
                          imageField="icon"
                          className={styles.cardImage}
                        />
                      </div>
                      <div className={styles.cardCtaContainer}>
                        <CTA
                          href={testimonial?.['@id']}
                          onClick={(
                            e: React.SyntheticEvent<HTMLLinkElement>,
                          ) => {
                            if (editMode) {
                              e.preventDefault();
                            }
                          }}
                          openLinkInNewTab={false}
                          ctaButtonStyle="button"
                        >
                          {intl.formatMessage(messages.learn_more_label)}
                        </CTA>
                      </div>
                    </div>

                    {!!testimonial.description && (
                      <div className={styles.cardBody}>
                        "{testimonial.description}"
                      </div>
                    )}
                    <div className={styles.cardTitle}>
                      {testimonial_owner}
                      {testimonial.referenced_product_info && (
                        <div>{testimonial?.referenced_product_info?.title}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
};

export default TestimonialSwiper;

const messages = defineMessages({
  learn_more_label: {
    id: 'learn_more_label',
    defaultMessage: 'Learn more',
  },
});
